<template>
    <KvModal ref="modal" id="monthly-invoice-generation-modal" size="large">
        <template #title>{{ t('invoices-generate_monthly_title') }}</template>
        <template #subtitle>{{ t('invoices-generate_monthly_subtitle') }}</template>
        <template #body>
            <div v-if="preview" class="monthly-generation-preview">
                <div class="monthly-generation-stats">
                    <div class="monthly-generation-stat">
                        <span>{{ t('invoices-generation_places') }}</span>
                        <strong>{{ summary.places_processed || 0 }}</strong>
                    </div>
                    <div class="monthly-generation-stat">
                        <span>{{ t('invoices-generation_to_generate') }}</span>
                        <strong>{{ selectedTargetsCount }}</strong>
                    </div>
                    <div class="monthly-generation-stat">
                        <span>{{ t('invoices-generation_drafts_to_delete') }}</span>
                        <strong>{{ selectedDraftsToDeleteCount }}</strong>
                    </div>
                    <div class="monthly-generation-stat">
                        <span>{{ t('invoices-generation_blocked') }}</span>
                        <strong>{{ summary.invoice_targets_blocked || 0 }}</strong>
                    </div>
                    <div class="monthly-generation-stat">
                        <span>{{ t('invoices-generation_without_plan') }}</span>
                        <strong>{{ summary.active_places_without_plan || 0 }}</strong>
                    </div>
                </div>

                <div v-if="executing" class="monthly-generation-running">
                    <span class="fa fa-spinner fa-spin mr-2"></span>
                    {{ t('invoices-generation_running') }}
                </div>

                <div v-if="preview.failed_places && preview.failed_places.length"
                    class="monthly-generation-alert error">
                    {{ t('invoices-generation_failed_places', { count: preview.failed_places.length }) }}
                </div>

                <div v-if="activePlacesWithoutPlan.length" class="monthly-generation-without-plan">
                    <button type="button" class="monthly-generation-without-plan-toggle"
                        @click="showActivePlacesWithoutPlan = !showActivePlacesWithoutPlan">
                        <span>
                            <span class="monthly-generation-section-title">
                                {{ t('invoices-generation_without_plan_title') }}
                            </span>
                            <span class="monthly-generation-section-subtitle">
                                {{ t('invoices-generation_without_plan_hint') }}
                            </span>
                        </span>
                        <span class="monthly-generation-toggle-icon"
                            :class="{ open: showActivePlacesWithoutPlan }">
                            <span class="fa fa-chevron-down"></span>
                        </span>
                    </button>
                    <div v-if="showActivePlacesWithoutPlan" class="monthly-generation-without-plan-list">
                        <div v-for="place in activePlacesWithoutPlan" :key="place.places_id"
                            class="monthly-generation-without-plan-item">
                            <div>
                                <div class="font-weight-500 text-dark">
                                    {{ place.place_name || `${t('invoices-establishment')} #${place.places_id}` }}
                                </div>
                                <div class="text-muted small">
                                    #{{ place.places_id }}
                                    <template v-if="place.slug"> · {{ place.slug }}</template>
                                    <template v-if="place.email"> · {{ place.email }}</template>
                                </div>
                            </div>
                            <span v-if="place.tax_id" class="monthly-generation-tax-id">{{ place.tax_id }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="generationTargets.length" class="monthly-generation-select-bar">
                    <span>{{ t('invoices-generation_selected', { selected: selectedTargetsCount, total: generationTargets.length }) }}</span>
                    <div class="monthly-generation-select-actions">
                        <button type="button" :disabled="executing" @click="toggleAllTargets">
                            {{ areAllTargetsSelected ? t('invoices-generation_deselect_all') : t('invoices-generation_select_all') }}
                        </button>
                    </div>
                </div>

                <div class="monthly-generation-list">
                    <div v-if="details.length === 0" class="monthly-generation-empty">
                        {{ t('invoices-generation_no_changes') }}
                    </div>
                    <div v-for="place in details" :key="place.places_id" class="monthly-generation-place">
                        <div class="monthly-generation-place-info">
                            <div class="font-weight-500 text-dark">
                                {{ place.place_name || `${t('invoices-establishment')} #${place.places_id}` }}
                            </div>
                            <div class="text-muted small">
                                {{ place.billing_type || place.reason || '-' }}
                                <template v-if="place.cycle_start && place.cycle_end">
                                    · {{ formatDate(place.cycle_start) }} - {{ formatDate(place.cycle_end) }}
                                </template>
                            </div>
                        </div>

                        <div v-if="place.status === 'ready'" class="monthly-generation-place-right">
                            <div class="monthly-generation-place-grid">
                                <span>{{ t('invoices-generation_to_generate') }}: <strong>{{
                                    selectedTargetsForPlace(place).length
                                }}</strong></span>
                                <span>{{ t('invoices-generation_drafts_to_delete') }}: <strong>{{
                                    selectedDraftsForPlace(place).length
                                }}</strong></span>
                                <span>{{ t('invoices-generation_blocked') }}: <strong>{{ (place.blocked_targets ||
                                    []).length }}</strong></span>
                            </div>

                            <div v-if="canTogglePlaceTargets(place)" class="monthly-generation-place-actions">
                                <button type="button"
                                    class="monthly-generation-link-btn"
                                    :disabled="executing"
                                    @click="togglePlaceTargets(place)">
                                    {{ areAllPlaceTargetsSelected(place) ? t('invoices-generation_deselect_all') : t('invoices-generation_select_all') }}
                                </button>
                            </div>

                            <div v-if="(place.targets_to_generate || []).length" class="monthly-generation-target-list">
                                <label v-for="target in place.targets_to_generate"
                                    :key="target.key"
                                    class="monthly-generation-target">
                                    <span :title="targetLabel(target, place)">
                                        {{ targetLabel(target, place) }}
                                    </span>
                                    <input type="checkbox"
                                        :checked="isTargetSelected(place, target)"
                                        :disabled="executing"
                                        @change="toggleTarget(place, target, $event.target.checked)">
                                </label>
                            </div>
                        </div>
                        <div v-else-if="place.reason || place.error" class="monthly-generation-place-reason">
                            {{ place.reason || place.error }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <kv-button variant="ghost" :disabled="executing" @click="close">
                {{ t('invoices-cancel') }}
            </kv-button>
            <kv-button v-if="preview && preview.summary && preview.summary.dry_run" variant="filled"
                prepend-icon="ui-check" :loading="executing" :disabled="selectedTargetsCount === 0"
                @click="confirmExecute">
                {{ t('invoices-generation_confirm') }}
            </kv-button>
        </template>
    </KvModal>
</template>

<script>
import KvModal from "../../components/modal/modal.vue";

export default {
    name: "MonthlyInvoiceGenerationModal",
    components: { KvModal },
    props: {
        preview: {
            type: Object,
            default: null
        },
        executing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showActivePlacesWithoutPlan: true,
            selectedTargets: {}
        };
    },
    watch: {
        preview: {
            immediate: true,
            handler() {
                this.initializeSelectedTargets();
            }
        }
    },
    computed: {
        summary() {
            return this.preview?.summary || {};
        },
        details() {
            return (this.preview?.details || []).filter(place => {
                if (place.status !== 'ready') return true;
                return (place.targets_to_generate || []).length > 0
                    || (place.draft_invoices_to_delete || []).length > 0
                    || (place.blocked_targets || []).length > 0;
            });
        },
        activePlacesWithoutPlan() {
            return this.preview?.active_places_without_plan || [];
        },
        generationTargets() {
            const targets = [];
            (this.preview?.details || []).forEach(place => {
                (place.targets_to_generate || []).forEach(target => {
                    targets.push({
                        places_id: place.places_id,
                        key: target.key,
                        uid: this.targetUid(place, target)
                    });
                });
            });
            return targets;
        },
        selectedTargetsCount() {
            return this.generationTargets.filter(target => this.selectedTargets[target.uid]).length;
        },
        selectedDraftsToDeleteCount() {
            return this.details.reduce((count, place) => count + this.selectedDraftsForPlace(place).length, 0);
        },
        areAllTargetsSelected() {
            return this.generationTargets.length > 0 && this.selectedTargetsCount === this.generationTargets.length;
        }
    },
    methods: {
        open() {
            this.$refs.modal && this.$refs.modal.open();
        },
        close() {
            this.$refs.modal && this.$refs.modal.close();
            this.$emit('close');
        },
        formatDate(date) {
            if (!date) return '-';
            const parts = String(date).slice(0, 10).split('-');
            if (parts.length !== 3) return date;
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        },
        initializeSelectedTargets() {
            const selected = {};
            (this.preview?.details || []).forEach(place => {
                (place.targets_to_generate || []).forEach(target => {
                    selected[this.targetUid(place, target)] = true;
                });
            });
            this.selectedTargets = selected;
        },
        targetUid(place, target) {
            return `${place.places_id}:${target.key}`;
        },
        isTargetSelected(place, target) {
            return !!this.selectedTargets[this.targetUid(place, target)];
        },
        toggleTarget(place, target, checked) {
            this.$set(this.selectedTargets, this.targetUid(place, target), checked);
        },
        selectAllTargets(checked) {
            this.generationTargets.forEach(target => {
                this.$set(this.selectedTargets, target.uid, checked);
            });
        },
        toggleAllTargets() {
            this.selectAllTargets(!this.areAllTargetsSelected);
        },
        canTogglePlaceTargets(place) {
            return Boolean(place.one_invoice_per_branch_plus_base) && (place.targets_to_generate || []).length > 1;
        },
        areAllPlaceTargetsSelected(place) {
            const targets = place.targets_to_generate || [];
            if (targets.length === 0) return false;
            return targets.every(target => this.isTargetSelected(place, target));
        },
        togglePlaceTargets(place, checked) {
            const shouldSelect = typeof checked === 'boolean'
                ? checked
                : !this.areAllPlaceTargetsSelected(place);
            (place.targets_to_generate || []).forEach(target => {
                this.$set(this.selectedTargets, this.targetUid(place, target), shouldSelect);
            });
        },
        selectedTargetsForPlace(place) {
            return (place.targets_to_generate || []).filter(target => this.isTargetSelected(place, target));
        },
        targetLabel(target, place) {
            const isMain = target.key === 'main' || target.places_branches_id === null;
            if (!isMain) {
                return target.label || target.branch_name || target.key;
            }

            const isMultiBranch = !place || Boolean(place.one_invoice_per_branch_plus_base);
            const mainBadge = this.t('invoices-generation_main_badge') || this.t('invoices-generation_main_target');
            const branchName = (target.branch_name || target.branch_title || '').trim();

            if (isMultiBranch && branchName) {
                const lowerBranch = branchName.toLowerCase();
                const lowerBadge = mainBadge.toLowerCase();

                if (lowerBranch === lowerBadge || /\((matriz|main|headquarters)\)/i.test(branchName)) {
                    return branchName;
                }

                return `${branchName} (${mainBadge})`;
            }

            return this.t('invoices-generation_main_target');
        },
        selectedDraftsForPlace(place) {
            return (place.draft_invoices_to_delete || []).filter(invoice => {
                return !!this.selectedTargets[`${place.places_id}:${invoice.target_key}`];
            });
        },
        selectedTargetsPayload() {
            const payload = [];
            (this.preview?.details || []).forEach(place => {
                this.selectedTargetsForPlace(place).forEach(target => {
                    payload.push({
                        places_id: place.places_id,
                        key: target.key
                    });
                });
            });
            return payload;
        },
        confirmExecute() {
            this.$emit('execute', this.selectedTargetsPayload());
        }
    }
};
</script>

<style lang="scss" scoped>
.monthly-generation-preview {
    text-align: left;
}

.monthly-generation-stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 16px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.monthly-generation-stat {
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 12px;
    background: #FFF;

    span {
        display: block;
        color: #6B7280;
        font-size: 12px;
        margin-bottom: 4px;
    }

    strong {
        color: #111827;
        font-size: 22px;
        line-height: 1;
    }
}

.monthly-generation-alert {
    border: 1px solid #BFDBFE;
    background: #EFF6FF;
    color: #1D4ED8;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    margin-bottom: 12px;

    &.error {
        border-color: #FECACA;
        background: #FEF2F2;
        color: #B91C1C;
    }
}

.monthly-generation-running {
    color: #374151;
    font-size: 13px;
    margin-bottom: 12px;
}

.monthly-generation-without-plan {
    border: 1px solid #FDE68A;
    background: #FFFBEB;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
}

.monthly-generation-section-title {
    display: block;
    color: #92400E;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 2px;
}

.monthly-generation-section-subtitle {
    display: block;
    color: #92400E;
    font-size: 12px;
    margin-bottom: 10px;
}

.monthly-generation-without-plan-toggle {
    appearance: none;
    border: none;
    background: transparent;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 0;
    text-align: left;
    cursor: pointer;
}

.monthly-generation-toggle-icon {
    color: #92400E;
    font-size: 12px;
    margin-top: 2px;
    transition: transform 0.2s ease;

    &.open {
        transform: rotate(180deg);
    }
}

.monthly-generation-without-plan-list {
    border: 1px solid #FDE68A;
    border-radius: 6px;
    background: #FFF;
    max-height: 160px;
    overflow-y: auto;
}

.monthly-generation-without-plan-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 10px;
    border-bottom: 1px solid #FEF3C7;

    &:last-child {
        border-bottom: none;
    }
}

.monthly-generation-tax-id {
    color: #78350F;
    font-size: 12px;
    white-space: nowrap;
}

.monthly-generation-select-bar {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    color: #374151;
    font-size: 13px;

    @media (max-width: 768px) {
        align-items: flex-start;
        flex-direction: column;
    }
}

.monthly-generation-select-actions {
    display: flex;
    gap: 8px;

    button {
        appearance: none;
        background: #FFF;
        border: 1px solid #D1D5DB;
        border-radius: 6px;
        color: #374151;
        cursor: pointer;
        font-size: 12px;
        line-height: 1;
        padding: 7px 10px;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.6;
        }
    }
}

.monthly-generation-list {
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    background: #FFF;
    max-height: 360px;
    overflow-y: auto;
}

.monthly-generation-empty {
    padding: 24px;
    color: #6B7280;
    text-align: center;
    font-size: 13px;
}

.monthly-generation-place {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(360px, 48%);
    align-items: center;
    gap: 16px;
    padding: 12px;
    border-bottom: 1px solid #F3F4F6;

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        align-items: start;
        gap: 10px;
    }
}

.monthly-generation-place-info {
    min-width: 0;
}

.monthly-generation-place-reason {
    color: #6B7280;
    font-size: 12px;
}

.monthly-generation-place-right {
    min-width: 0;
}

.monthly-generation-place-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    color: #6B7280;
    font-size: 12px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.monthly-generation-place-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 6px;
    margin-bottom: 4px;
}

.monthly-generation-link-btn {
    appearance: none;
    background: transparent;
    border: none;
    color: #4B5563;
    cursor: pointer;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    padding: 2px 4px;
    border-radius: 4px;
    transition: color 0.15s ease, background-color 0.15s ease;

    &:hover:not(:disabled) {
        color: #111827;
        background-color: #F3F4F6;
    }

    &:focus {
        outline: none;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}

.monthly-generation-target-list {
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    margin-top: 8px;
    overflow: hidden;
}

.monthly-generation-place-actions + .monthly-generation-target-list {
    margin-top: 0;
}

.monthly-generation-target {
    align-items: center;
    display: flex;
    gap: 10px;
    justify-content: space-between;
    margin: 0;
    padding: 8px 10px;
    color: #374151;
    font-size: 12px;
    font-weight: 400;
    border-bottom: 1px solid #F3F4F6;

    &:last-child {
        border-bottom: none;
    }

    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    input {
        accent-color: #209941;
        flex: 0 0 auto;
        height: 16px;
        width: 16px;
    }
}
</style>
